import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { 
  ArrowLeft, MapPin, Phone, Mail, AlertTriangle, User, 
  Heart, Activity, Clock, FileText, Play, ChevronRight,
  Home, Shield, Clipboard, Users
} from "lucide-react";

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const loadClient = async () => {
    try {
      const { data } = await api.get(`/employee/clients/${clientId}`);
      setClient(data);
    } catch (err) {
      console.error("Failed to load client:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <div className="font-medium">Client Not Found</div>
          <button 
            onClick={() => navigate(-1)} 
            className="mt-4 text-[#06372B] font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const carePlan = client.care_plan;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-lg truncate">{client.first_name} {client.last_name}</h1>
              <div className="text-sm text-white/70 truncate">{client.address}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Clock In Button (sticky at top) */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-[72px] z-30">
        <div className="max-w-lg mx-auto">
          <Link
            to={`/employee/dashboard?clockIn=${clientId}`}
            className="w-full py-3 px-4 rounded-xl bg-[#06372B] text-white font-semibold text-center flex items-center justify-center gap-2"
            data-testid="clock-in-btn"
          >
            <Play className="w-4 h-4" />
            Clock In for This Client
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[144px] z-20">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex gap-1">
            {[
              { key: "info", label: "Info" },
              { key: "care", label: "Care Plan" },
              { key: "contacts", label: "Contacts" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key 
                    ? "border-[#06372B] text-[#06372B]" 
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Info Tab */}
        {activeTab === "info" && (
          <div className="space-y-4">
            {/* Contact Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold">Contact Information</h2>
              </div>
              <div className="p-4 space-y-3">
                {client.address && (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Address</div>
                      <div className="font-medium">{client.address}</div>
                      {(client.city || client.state) && (
                        <div className="text-sm text-gray-600">
                          {[client.city, client.state, client.zip_code].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {client.phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Phone</div>
                      <a href={`tel:${client.phone}`} className="font-medium text-[#06372B]">{client.phone}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Health Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold">Health & Safety</h2>
              </div>
              <div className="p-4 space-y-4">
                {/* Mobility */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Mobility</span>
                  </div>
                  <span className="font-medium capitalize">{client.mobility_status?.replace(/_/g, " ") || "Independent"}</span>
                </div>

                {/* Fall Risk */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Fall Risk</span>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    client.fall_risk === "high" ? "bg-red-50 text-red-700" :
                    client.fall_risk === "moderate" ? "bg-amber-50 text-amber-700" :
                    "bg-green-50 text-green-700"
                  }`}>
                    {client.fall_risk || "Low"}
                  </span>
                </div>

                {/* Cognitive Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm">Cognitive</span>
                  </div>
                  <span className="font-medium capitalize">{client.cognitive_status?.replace(/_/g, " ") || "Alert"}</span>
                </div>

                {/* Allergies */}
                {client.allergies?.length > 0 && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-red-700 mb-2">
                      <Heart className="w-4 h-4" />
                      <span className="font-medium text-sm">Allergies</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {client.allergies.map((allergy, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          {allergy}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Special Instructions */}
            {(client.special_instructions || client.preferred_routines) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="font-semibold">Special Instructions</h2>
                </div>
                <div className="p-4 space-y-3">
                  {client.preferred_routines && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Preferred Routines</div>
                      <p className="text-gray-700">{client.preferred_routines}</p>
                    </div>
                  )}
                  {client.special_instructions && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Notes</div>
                      <p className="text-gray-700">{client.special_instructions}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Care Plan Tab */}
        {activeTab === "care" && (
          <div className="space-y-4">
            {!carePlan ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <Clipboard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <div className="font-medium">No Care Plan</div>
                <div className="text-sm text-gray-500 mt-1">Contact your supervisor for care plan details.</div>
              </div>
            ) : (
              <>
                {/* ADL Tasks */}
                {carePlan.adl_tasks?.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-blue-50">
                      <h2 className="font-semibold text-blue-900">Activities of Daily Living</h2>
                      <p className="text-xs text-blue-700 mt-0.5">H = Hands-on, S = Stand-by Assistance</p>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {carePlan.adl_tasks.filter(t => t.is_authorized).map((task, i) => (
                        <div key={i} className="px-4 py-3 flex items-center justify-between">
                          <span className="capitalize">{task.task_name.replace(/_/g, " ")}</span>
                          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                            Authorized
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Homemaker Tasks */}
                {carePlan.homemaker_tasks?.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-emerald-50">
                      <h2 className="font-semibold text-emerald-900">Homemaker Services</h2>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {carePlan.homemaker_tasks.filter(t => t.is_authorized).map((task, i) => (
                        <div key={i} className="px-4 py-3 flex items-center justify-between">
                          <span className="capitalize">{task.task_name.replace(/_/g, " ")}</span>
                          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-medium">
                            Authorized
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supervision */}
                {carePlan.supervision_required && (
                  <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-amber-700 mt-0.5" />
                      <div>
                        <div className="font-semibold text-amber-900">Supervision Required</div>
                        {carePlan.supervision_notes && (
                          <p className="text-sm text-amber-800 mt-1">{carePlan.supervision_notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="space-y-4">
            {client.emergency_contacts?.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <div className="font-medium">No Emergency Contacts</div>
                <div className="text-sm text-gray-500 mt-1">Contact information not available.</div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="font-semibold">Emergency Contacts</h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {client.emergency_contacts.map((contact, i) => (
                    <div key={i} className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.relationship}</div>
                          {contact.is_authorized_rep && (
                            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">
                              Authorized Representative
                            </span>
                          )}
                        </div>
                        <a 
                          href={`tel:${contact.phone}`} 
                          className="p-2.5 rounded-xl bg-[#06372B] text-white"
                        >
                          <Phone className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
